import { useFormContext } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { CrmRecordSummary, CrmRecordType } from "@/lib/api/crm/masters";
import type { CrmRules } from "@/lib/api/crm/rules";
import type { AvailableOnboardingInstanceInfo } from "@/lib/api/customer";
import { MasterCombobox } from "../../components/MasterCombobox";
import { recordTypeLabels, recordTypeOrder } from "../../recordTypes";
import { createsCustomer, requiresClientId, type RecordFormValues } from "../recordFormSchema";
import { OnboardingInstanceSelect } from "./OnboardingInstanceSelect";
import { TagPicker } from "./TagPicker";

function ReadOnlyValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium leading-none">{label}</p>
      <p className="flex h-10 items-center rounded-md border bg-gray-50 px-3 text-sm text-gray-700">{value}</p>
    </div>
  );
}

/** "Add a producer, non-client, client or end user first." */
function missingOwnerHint(ownerTypes: CrmRecordType[]): string {
  const names = ownerTypes.map((type) => recordTypeLabels[type].toLowerCase());
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}` : names[0] ?? "";
  return `Add a ${list} first.`;
}

/** Record Group, Type, Belongs to, Client ID and Onboarding instance (create only), Name, Short name and record tags. */
export function BaseFieldsSection({
  mode,
  rules,
  groupRecords,
  groupName,
  isGroupLocked,
  allowNewGroup,
  allowedRecordTypes,
  disabled,
  recordTagNote,
  onGroupChange,
  onRecordTypeChange,
  onRecordTagsChange,
  onOnboardingChange,
  onNewGroupChange,
}: {
  mode: "create" | "update";
  rules: CrmRules;
  /** Records in the chosen group. */
  groupRecords: CrmRecordSummary[];
  groupName: string | null;
  isGroupLocked: boolean;
  /** Create: offer creating a new record group with the record instead of choosing one. */
  allowNewGroup: boolean;
  /** Create: only these types are offered; null offers every type. */
  allowedRecordTypes: readonly CrmRecordType[] | null;
  disabled: boolean;
  /** Shown under the tags after a type change removed some. */
  recordTagNote: string | null;
  onGroupChange: (masterId: string) => void;
  onRecordTypeChange: (recordType: CrmRecordType) => void;
  onRecordTagsChange: (tagIds: string[]) => void;
  /** Create: an onboarding instance was picked (null when cleared). */
  onOnboardingChange: (instance: AvailableOnboardingInstanceInfo | null) => void;
  /** Create: "Create a new record group" was ticked or unticked. */
  onNewGroupChange: (checked: boolean) => void;
}) {
  const form = useFormContext<RecordFormValues>();
  const recordType = form.watch("record_type");
  const ownerId = form.watch("owner_id");
  const newGroup = form.watch("new_group");
  const ownerTypes = recordType ? rules.record_types[recordType].owner_types : null;
  const isCreate = mode === "create";

  // Types come from GET /crm/rules, in display order. A child type needs an owner already in the group.
  const ruleTypes = recordTypeOrder.filter((type) =>
    type in rules.record_types && (allowedRecordTypes === null || allowedRecordTypes.includes(type)));
  const canBeOwned = (type: CrmRecordType): boolean => {
    const allowedOwners = rules.record_types[type].owner_types;
    return allowedOwners === null || groupRecords.some((record) => allowedOwners.includes(record.record_type));
  };
  const typeOptions = ruleTypes.filter(canBeOwned).map((type) => ({ value: type, label: recordTypeLabels[type] }));
  const unavailableTypes = ruleTypes.filter((type) => !canBeOwned(type));
  const ownerOptions = groupRecords
    .filter((record) => ownerTypes?.includes(record.record_type))
    .map((record) => ({ value: record.id, label: `${record.long_name} (${recordTypeLabels[record.record_type]})` }));
  const ownerName = groupRecords.find((record) => record.id === ownerId)?.long_name ?? "—";

  return (
    <div className="grid gap-5 md:grid-cols-2">
      {isCreate && allowNewGroup && !isGroupLocked && (
        <label className="flex items-center gap-2 text-sm font-medium md:col-span-2" htmlFor="crm-form-new-group">
          <Checkbox
            id="crm-form-new-group"
            checked={newGroup !== null}
            onCheckedChange={(checked) => onNewGroupChange(checked === true)}
            disabled={disabled}
            data-testid="crm-form-new-group"
          />
          Create a new record group
        </label>
      )}

      {isCreate && newGroup !== null && (
        <>
          <FormField
            control={form.control}
            name="new_group.long_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Group name *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      // Group short name follows Group name until it's typed in, like the record's.
                      if (!form.getFieldState("new_group.short_name").isDirty) {
                        form.setValue("new_group.short_name", event.target.value);
                      }
                    }}
                    disabled={disabled}
                    data-testid="crm-form-new-group-long-name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="new_group.short_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Group short name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Same as group name" disabled={disabled} data-testid="crm-form-new-group-short-name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}

      {isCreate && newGroup === null && (
        <FormField
          control={form.control}
          name="master_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Record Group *</FormLabel>
              {isGroupLocked ? (
                <p className="flex h-10 items-center rounded-md border bg-gray-50 px-3 text-sm text-gray-700" data-testid="crm-form-group-locked">
                  {groupName ?? "—"}
                </p>
              ) : (
                <FormControl>
                  <div className="space-y-1">
                    <MasterCombobox
                      id="crm-form-group"
                      isModal
                      placeholder="Choose a record group"
                      popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                      value={field.value}
                      onValueChange={(masterId: string) => {
                        field.onChange(masterId);
                        onGroupChange(masterId);
                      }}
                      disabled={disabled}
                    />
                  </div>
                </FormControl>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {isCreate ? (
        <FormField
          control={form.control}
          name="record_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type *</FormLabel>
              <FormControl>
                <div>
                  <Combobox
                    id="crm-form-type"
                    isModal
                    dwimOnChange
                    className="w-full"
                    virtualizeList
                    popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                    placeholder="Choose a type"
                    options={typeOptions}
                    value={field.value}
                    onValueChange={(value: string) => {
                      const nextType = typeOptions.find((option) => option.value === value)?.value;
                      if (nextType) onRecordTypeChange(nextType);
                      field.onBlur();
                    }}
                    disabled={disabled}
                  />
                </div>
              </FormControl>
              {unavailableTypes.length > 0 && (
                <ul className="space-y-0.5 text-xs text-gray-500" data-testid="crm-form-type-hints">
                  {unavailableTypes.map((type) => (
                    <li key={type}>
                      {recordTypeLabels[type]}: {missingOwnerHint(rules.record_types[type].owner_types ?? [])}
                    </li>
                  ))}
                </ul>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      ) : (
        <ReadOnlyValue label="Type" value={recordType ? recordTypeLabels[recordType] : "—"} />
      )}

      {ownerTypes && (isCreate ? (
        <FormField
          control={form.control}
          name="owner_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Belongs to *</FormLabel>
              <FormControl>
                <div>
                  <Combobox
                    id="crm-form-owner"
                    isModal
                    dwimOnChange
                    className="w-full"
                    virtualizeList
                    popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                    placeholder="Choose a record"
                    options={ownerOptions}
                    value={field.value}
                    onValueChange={(value: string) => {
                      field.onChange(value);
                      field.onBlur();
                    }}
                    disabled={disabled}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : (
        <ReadOnlyValue label="Belongs to" value={ownerName} />
      ))}

      <FormField
        control={form.control}
        name="long_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name *</FormLabel>
            <FormControl>
              <Input
                {...field}
                onChange={(event) => {
                  field.onChange(event);
                  // On create, Short name follows Name until it's typed in.
                  if (isCreate && !form.getFieldState("short_name").isDirty) {
                    form.setValue("short_name", event.target.value);
                  }
                }}
                disabled={disabled}
                data-testid="crm-form-long-name"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="short_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Short name</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Same as name" disabled={disabled} data-testid="crm-form-short-name" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="tag_ids"
        render={({ field }) => (
          <FormItem className="md:col-span-1">
            <FormLabel>Tags</FormLabel>
            <FormControl>
              <div>
                {recordType ? (
                  <TagPicker
                    id="crm-form-record-tags"
                    rules={rules}
                    target={{ type: "record", recordType }}
                    value={field.value}
                    onChange={onRecordTagsChange}
                    disabled={disabled}
                  />
                ) : (
                  <p className="text-sm text-gray-400">Choose a type first</p>
                )}
              </div>
            </FormControl>
            {recordTagNote && <p className="text-xs text-amber-700">{recordTagNote}</p>}
            <FormMessage />
          </FormItem>
        )}
      />
      {isCreate && requiresClientId(rules, recordType) && (
        <FormField
          control={form.control}
          name="client_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Client ID *</FormLabel>
              <FormControl>
                <Input {...field} disabled={disabled} data-testid="crm-form-client-id" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {isCreate && createsCustomer(rules, recordType) && (
        <FormField
          control={form.control}
          name="onboarding"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Onboarding instance</FormLabel>
              <FormControl>
                <div data-testid="crm-form-onboarding">
                  <OnboardingInstanceSelect
                    id="crm-form-onboarding-instance"
                    value={field.value?.instance_id ?? ""}
                    onChange={(instance: AvailableOnboardingInstanceInfo | null) => {
                      onOnboardingChange(instance);
                      field.onBlur();
                    }}
                    disabled={disabled}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
